'use client';

import React from 'react';
import CustomLink from 'components/shared/CustomLink';
import type { CustomLinkProps } from 'components/shared/CustomLink';
import type { LinkProps } from 'next/link';
import { CTAButton } from 'components/shared/CTAComponent';
import ctaStyles from './CTAComponent.module.css';
import styles from './ButtonV2.module.css';

// TODO: Remove dependencies for CTAButton

type ButtonVariant =
  | 'white'
  | 'blue'
  | 'transparentDark'
  | 'transparentLight'
  | 'glassLight'
  | 'glassDark'
  | 'flat'
  | 'black';

export interface ButtonV2Props
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * 'white' | 'blue' | 'transparentDark' | 'transparentLight' | 'glassLight' | 'glassDark'
   */
  variant?: ButtonVariant;
  /** Optional text/element label. Used when children not provided. */
  label?: React.ReactNode;
  /** When provided, the button will be wrapped in CustomLink. */
  href?: LinkProps['href'];
  /** Additional props to forward to CustomLink when href is provided. */
  linkProps?: Omit<CustomLinkProps, 'href' | 'children'>;
  /** Force element type regardless of href/onClick. */
  componentType?: 'button' | 'link';
  /** Optional icon rendered before the label */
  startIcon?: React.ReactNode;
  /** Optional icon rendered after the label */
  endIcon?: React.ReactNode;
  /** Tracking attribute for analytics */
  'data-buttontype'?: string;
}

export const ButtonV2 = React.forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      children,
      label,
      href,
      linkProps,
      componentType,
      onClick,
      startIcon,
      endIcon,
      variant,
      className,
      ...rest
    },
    ref,
  ) => {
    const content = label ?? children;
    const shouldRenderAsLink =
      (componentType ?? (href ? 'link' : 'button')) === 'link';

    const buttonEl = (
      <CTAButton
        ref={ref}
        onClick={onClick}
        {...rest}
        type={(rest as any).type ?? 'button'}
        data-variant={variant}
        className={[styles.styledCta, className].filter(Boolean).join(' ')}
      >
        {startIcon ? (
          <span className={styles.iconSlot}>{startIcon}</span>
        ) : null}
        <span className={styles.label}>{content}</span>
        {endIcon ? <span className={styles.iconSlot}>{endIcon}</span> : null}
      </CTAButton>
    );

    if (shouldRenderAsLink && typeof href === 'string' && href.startsWith('#')) {
      return (
        <a
          href={href}
          onClick={onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
          data-variant={variant}
          data-buttontype={rest['data-buttontype']}
          className={[ctaStyles.ctaButton, styles.styledCta, className]
            .filter(Boolean)
            .join(' ')}
        >
          {startIcon ? (
            <span className={styles.iconSlot}>{startIcon}</span>
          ) : null}
          <span className={styles.label}>{content}</span>
          {endIcon ? <span className={styles.iconSlot}>{endIcon}</span> : null}
        </a>
      );
    }

    if (shouldRenderAsLink && href) {
      return (
        <CustomLink href={href as LinkProps['href']} {...linkProps}>
          {buttonEl}
        </CustomLink>
      );
    }

    return buttonEl;
  },
);

ButtonV2.displayName = 'ButtonV2';

export default ButtonV2;
